import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ProgressDot from "./ProgressDot";

const ProgressDotGroup = ({
  totalSteps,
  currentStep,
  isReviewed = false,
  listAnwserErr = [],
  listQuestion = [],
  questAnswers = {},
  submittedSteps = [],
  onStepChange,
  currentStepData,
  selectedOption,
  selectedOptions,
  answerText,
  additionalComment,
  otherOptionTexts,
  hasOtherError,
  maxCheckboxOptions,
}) => {
  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startTranslateXRef = useRef(0);
  const hasShownToastRef = useRef(false);
  const { t } = useTranslation();

  const [translateX, setTranslateX] = useState(0);
  const [dragStep, setDragStep] = useState(currentStep);

  const effectiveTotalSteps = listQuestion.length || totalSteps;
  const answerErrSteps = listAnwserErr.map((err) => err.step_index || 1);
  const isCurrentStepError =
    isReviewed && answerErrSteps.includes(currentStep + 1);

  const validateCurrentStep = useCallback(() => {
    if (!currentStepData) {
      const hasAnswerText = answerText && answerText.trim().length > 0;
      return (
        currentStep === 1 && hasAnswerText && answerText.trim().length >= 100
      );
    }

    const ANSWER_TEXT_TYPES = ["short", "long"];
    const ANSWER_OPTION_TYPES = ["mcq", "checkbox"];
    const ANSWER_SLIDER_TYPES = ["slider"];
    const ANSWER_YESNO_TYPES = ["yes_no"];

    const hasAnswer = () => {
      if (ANSWER_TEXT_TYPES.includes(currentStepData.type)) {
        return !!(answerText && answerText.trim());
      }
      if (
        ANSWER_OPTION_TYPES.includes(currentStepData.type) ||
        ANSWER_SLIDER_TYPES.includes(currentStepData.type) ||
        ANSWER_YESNO_TYPES.includes(currentStepData.type)
      ) {
        if (currentStepData.type === "checkbox") {
          return selectedOptions && selectedOptions.length > 0;
        }
        return !!(selectedOption && selectedOption.id);
      }
      return false;
    };

    const hasAnswerResult = hasAnswer();

    if (currentStepData?.is_required && !hasAnswerResult) {
      return false;
    }

    if (!hasAnswerResult) {
      return false;
    }

    if (currentStepData.type === "checkbox") {
      const selectedCount = selectedOptions ? selectedOptions.length : 0;
      const requiredCount = maxCheckboxOptions;
      if (
        maxCheckboxOptions !== undefined &&
        maxCheckboxOptions !== null &&
        maxCheckboxOptions !== 0 &&
        selectedCount !== requiredCount
      ) {
        return false;
      }
    }

    const isAnswerInvalid =
      (ANSWER_TEXT_TYPES.includes(currentStepData.type) &&
        currentStepData.type === "long" &&
        (answerText || "").trim().length < 100) ||
      (ANSWER_TEXT_TYPES.includes(currentStepData.type) &&
        currentStepData.type === "short" &&
        (answerText || "").trim().length < 1) ||
      (currentStepData.is_additional_comment &&
        (additionalComment || "").trim().length < 50);

    if (isAnswerInvalid) {
      return false;
    }

    if (
      currentStepData?.is_additional_comment &&
      currentStepData.is_required_additional_comment &&
      !additionalComment
    ) {
      return false;
    }

    if (hasOtherError) {
      return false;
    }

    return true;
  }, [
    currentStepData,
    answerText,
    additionalComment,
    selectedOption,
    selectedOptions,
    hasOtherError,
    currentStep,
    maxCheckboxOptions,
  ]);

  const showValidationToast = useCallback(() => {
    if (hasShownToastRef.current) return;

    if (!currentStepData) {
      const hasAnswerText = answerText && answerText.trim().length > 0;
      if (!hasAnswerText) {
        toast.error(t("toast.error.thisQuestIsRequired"));
        hasShownToastRef.current = true;
        return;
      }
      toast.error("Please provide a valid answer before proceeding.");
      hasShownToastRef.current = true;
      return;
    }

    const ANSWER_TEXT_TYPES = ["short", "long"];
    const ANSWER_OPTION_TYPES = ["mcq", "checkbox"];
    const ANSWER_SLIDER_TYPES = ["slider"];
    const ANSWER_YESNO_TYPES = ["yes_no"];

    const hasAnswer = () => {
      if (ANSWER_TEXT_TYPES.includes(currentStepData.type)) {
        return !!(answerText && answerText.trim());
      }
      if (
        ANSWER_OPTION_TYPES.includes(currentStepData.type) ||
        ANSWER_SLIDER_TYPES.includes(currentStepData.type) ||
        ANSWER_YESNO_TYPES.includes(currentStepData.type)
      ) {
        if (currentStepData.type === "checkbox") {
          return selectedOptions && selectedOptions.length > 0;
        }
        return !!(selectedOption && selectedOption.id);
      }
      return false;
    };

    const hasAnswerResult = hasAnswer();

    if (currentStepData?.is_required && !hasAnswerResult) {
      toast.error(t("toast.error.thisQuestIsRequired"));
      hasShownToastRef.current = true;
      return;
    }

    if (!hasAnswerResult) {
      toast.error(t("toast.error.thisQuestIsRequired"));
      hasShownToastRef.current = true;
      return;
    }

    if (currentStepData.type === "checkbox") {
      const selectedCount = selectedOptions ? selectedOptions.length : 0;
      const requiredCount = maxCheckboxOptions;
      if (
        maxCheckboxOptions !== undefined &&
        maxCheckboxOptions !== null &&
        maxCheckboxOptions !== 0
      ) {
        if (selectedCount !== requiredCount) {
          toast.error(
            `Please select exactly ${requiredCount} options. You have selected ${selectedCount}.`
          );
          hasShownToastRef.current = true;
          return;
        }
      }
    }

    if (
      ANSWER_TEXT_TYPES.includes(currentStepData.type) &&
      currentStepData.type === "long" &&
      (answerText || "").trim().length < 100
    ) {
      toast.error("Please provide at least 100 characters for your answer.");
      hasShownToastRef.current = true;
      return;
    }

    if (
      ANSWER_TEXT_TYPES.includes(currentStepData.type) &&
      currentStepData.type === "short" &&
      (answerText || "").trim().length < 20
    ) {
      toast.error(t("toast.error.thisQuestIsRequired"));
      hasShownToastRef.current = true;
      return;
    }

    if (
      currentStepData.is_additional_comment &&
      (additionalComment || "").trim().length < 50
    ) {
      toast.error(
        "Please provide at least 50 characters for additional comment."
      );
      hasShownToastRef.current = true;
      return;
    }

    if (
      currentStepData?.is_additional_comment &&
      currentStepData.is_required_additional_comment &&
      !additionalComment
    ) {
      toast.error("Additional comment is required for this question.");
      hasShownToastRef.current = true;
      return;
    }

    if (hasOtherError) {
      toast.error("Please provide valid text for other option.");
      hasShownToastRef.current = true;
      return;
    }

    toast.error("Please provide a valid answer before proceeding.");
    hasShownToastRef.current = true;
  }, [
    currentStepData,
    answerText,
    additionalComment,
    selectedOption,
    selectedOptions,
    hasOtherError,
    maxCheckboxOptions,
    t,
  ]);

  const getMaxAllowedStep = useCallback(() => {
    if (isReviewed) {
      return effectiveTotalSteps;
    }

    const hasQuestAnswers = Object.keys(questAnswers).length > 0;
    const isPauseResumeScenario = hasQuestAnswers && currentStep === 1;

    if (isPauseResumeScenario) {
      const maxSubmittedStep =
        submittedSteps.length > 0
          ? Math.max(
              ...submittedSteps.filter((step) => step <= effectiveTotalSteps)
            )
          : 0;

      let maxAnsweredStep = 1;
      if (listQuestion && Array.isArray(listQuestion)) {
        for (let i = 0; i < listQuestion.length; i++) {
          const question = listQuestion[i];
          const stepIndex = i + 2;
          const hasStoredAnswer = question.id && !!questAnswers[question.id];
          const isSubmitted = submittedSteps.includes(stepIndex);
          if (hasStoredAnswer || isSubmitted) {
            maxAnsweredStep = stepIndex;
          } else {
            break;
          }
        }
      }
      return Math.max(maxAnsweredStep, maxSubmittedStep, 1);
    }

    let highestAnsweredStep = 0;
    const step1Submitted = submittedSteps.includes(1);
    const hasStep1Answer = (() => {
      if (currentStep === 1) {
        if (currentStepData) {
          const ANSWER_TEXT_TYPES = ["short", "long"];
          const ANSWER_OPTION_TYPES = ["mcq", "checkbox"];
          const ANSWER_SLIDER_TYPES = ["slider"];
          const ANSWER_YESNO_TYPES = ["yes_no"];
          if (ANSWER_TEXT_TYPES.includes(currentStepData.type)) {
            return !!(answerText && answerText.trim());
          }
          if (
            ANSWER_OPTION_TYPES.includes(currentStepData.type) ||
            ANSWER_SLIDER_TYPES.includes(currentStepData.type) ||
            ANSWER_YESNO_TYPES.includes(currentStepData.type)
          ) {
            if (currentStepData.type === "checkbox") {
              return selectedOptions && selectedOptions.length > 0;
            }
            return !!(selectedOption && selectedOption.id);
          }
        }
        return answerText && answerText.trim().length > 0;
      }
      return answerText && answerText.trim().length > 0;
    })();

    if (step1Submitted || (currentStep === 1 && hasStep1Answer)) {
      highestAnsweredStep = 1;
    } else {
      return 1;
    }

    if (listQuestion && Array.isArray(listQuestion)) {
      for (let i = 0; i < listQuestion.length; i++) {
        const stepIndex = i + 2;
        const isSubmitted = submittedSteps.includes(stepIndex);
        let hasActualAnswer = isSubmitted;

        if (stepIndex === currentStep) {
          const hasCurrentInput = (() => {
            const hasValidSelectedOption =
              selectedOption &&
              selectedOption.id !== null &&
              selectedOption.id !== undefined;
            const hasValidSelectedOptions =
              selectedOptions &&
              selectedOptions.length > 0 &&
              selectedOptions.some(
                (opt) => opt.id !== null && opt.id !== undefined
              );
            const hasValidAnswerText =
              answerText && answerText.trim().length > 0;
            const hasValidAdditionalComment =
              additionalComment && additionalComment.trim().length > 0;
            return (
              hasValidSelectedOption ||
              hasValidSelectedOptions ||
              hasValidAnswerText ||
              hasValidAdditionalComment
            );
          })();
          if (hasCurrentInput) {
            hasActualAnswer = true;
          }
        }
        if (hasActualAnswer) {
          highestAnsweredStep = stepIndex;
        }
      }
    }

    let maxAllowed =
      currentStep === highestAnsweredStep + 1 &&
      (selectedOption?.id ||
        (selectedOptions?.length > 0 &&
          selectedOptions.some(
            (opt) => opt.id !== null && opt.id !== undefined
          )) ||
        (answerText && answerText.trim().length > 0) ||
        (additionalComment && additionalComment.trim().length > 0))
        ? currentStep
        : Math.min(highestAnsweredStep + 1, effectiveTotalSteps);

    if (isCurrentStepError) {
      maxAllowed = Math.min(maxAllowed, currentStep);
    }

    return maxAllowed;
  }, [
    isReviewed,
    effectiveTotalSteps,
    currentStep,
    listQuestion,
    submittedSteps,
    questAnswers,
    answerText,
    selectedOption,
    selectedOptions,
    additionalComment,
    currentStepData,
    isCurrentStepError,
  ]);

  useEffect(() => {
    if (isDraggingRef.current || !containerRef.current) return;
    const containerWidth = containerRef.current.clientWidth;
    const dotSpacing = 28;
    const centerOffset = containerWidth / 2 - dotSpacing / 2;
    const newTranslateX = centerOffset - (currentStep - 1) * dotSpacing;
    setTranslateX(newTranslateX);
    setDragStep(currentStep);
  }, [currentStep, effectiveTotalSteps]);

  const handleStart = useCallback(
    (clientX, clientY) => {
      isDraggingRef.current = true;
      hasShownToastRef.current = false;
      startXRef.current = clientX;
      startYRef.current = clientY;
      startTranslateXRef.current = translateX;
      if (containerRef.current) {
        const innerContainer = containerRef.current.querySelector("div");
        if (innerContainer) innerContainer.style.transition = "none";
      }
    },
    [translateX]
  );

  const handleMove = useCallback(
    (clientX, clientY) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const deltaX = clientX - startXRef.current;
      const deltaY = clientY - startYRef.current;

      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        let newTranslateX = startTranslateXRef.current + deltaX;
        const containerWidth = containerRef.current.clientWidth;
        const dotSpacing = 28;
        const centerOffset = containerWidth / 2 - dotSpacing / 2;
        const currentOffsetFromStart = centerOffset - newTranslateX;
        const targetStep = Math.round(currentOffsetFromStart / dotSpacing) + 1;

        const isDragForward = targetStep > currentStep;
        const isDragBackward = targetStep < currentStep;

        const isCurrentStepValid = validateCurrentStep();

        if (isDragForward && !isCurrentStepValid) {
          if (!hasShownToastRef.current) {
            showValidationToast();
          }
          return;
        }

        const hasQuestAnswers = Object.keys(questAnswers).length > 0;
        const isPauseResumeScenario = hasQuestAnswers && currentStep === 1;

        const getAnsweredSteps = () => {
          const answeredSteps = [1];
          if (listQuestion && Array.isArray(listQuestion)) {
            for (let i = 0; i < listQuestion.length; i++) {
              const question = listQuestion[i];
              const stepIndex = i + 2;
              const hasStoredAnswer =
                question.id && !!questAnswers[question.id];
              const isSubmitted = submittedSteps.includes(stepIndex);
              if (hasStoredAnswer || isSubmitted) {
                answeredSteps.push(stepIndex);
              } else {
                break;
              }
            }
          }
          return answeredSteps;
        };

        let shouldAllowFreeDrag = isReviewed
          ? isDragBackward || isCurrentStepValid
          : isPauseResumeScenario &&
            isDragBackward &&
            getAnsweredSteps().includes(targetStep);

        const rightBound = centerOffset;
        let leftBound;
        let maxAllowedStep;

        if (shouldAllowFreeDrag) {
          if (isReviewed) {
            leftBound = centerOffset - (effectiveTotalSteps - 1) * dotSpacing;
            maxAllowedStep = effectiveTotalSteps;
          } else if (isPauseResumeScenario) {
            const answeredSteps = getAnsweredSteps();
            const maxAnsweredStep = Math.max(...answeredSteps);
            leftBound = centerOffset - (maxAnsweredStep - 1) * dotSpacing;
            maxAllowedStep = maxAnsweredStep;
          }
        } else {
          maxAllowedStep = getMaxAllowedStep();
          leftBound =
            currentStep === 1 && !isDragForward
              ? centerOffset
              : centerOffset - (maxAllowedStep - 1) * dotSpacing;
        }

        newTranslateX = Math.max(
          leftBound,
          Math.min(newTranslateX, rightBound)
        );
        setTranslateX(newTranslateX);

        const newStep =
          Math.round((centerOffset - newTranslateX) / dotSpacing) + 1;
        const constrainedStep = shouldAllowFreeDrag
          ? isReviewed
            ? Math.max(1, Math.min(newStep, effectiveTotalSteps))
            : Math.max(1, Math.min(newStep, Math.max(...getAnsweredSteps())))
          : currentStep === 1 && !isDragForward
          ? 1
          : Math.max(1, Math.min(newStep, maxAllowedStep));

        setDragStep(constrainedStep);
      }
    },
    [
      currentStep,
      effectiveTotalSteps,
      getMaxAllowedStep,
      showValidationToast,
      isReviewed,
      validateCurrentStep,
      questAnswers,
      submittedSteps,
      listQuestion,
    ]
  );

  const handleEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    hasShownToastRef.current = false;

    if (containerRef.current) {
      const innerContainer = containerRef.current.querySelector("div");
      if (innerContainer)
        innerContainer.style.transition = "transform 0.3s ease-out";
    }

    let validDragStep =
      dragStep > currentStep
        ? Math.min(dragStep, getMaxAllowedStep())
        : Math.max(1, Math.min(dragStep, effectiveTotalSteps));

    if (validDragStep !== currentStep) {
      onStepChange?.(validDragStep, {
        skipValidation: validDragStep < currentStep,
      });
    } else {
      const containerWidth = containerRef.current.clientWidth;
      const dotSpacing = 28;
      const centerOffset = containerWidth / 2 - dotSpacing / 2;
      const correctTranslateX = centerOffset - (currentStep - 1) * dotSpacing;
      setTranslateX(correctTranslateX);
      setDragStep(currentStep);
    }
  }, [
    currentStep,
    dragStep,
    onStepChange,
    getMaxAllowedStep,
    effectiveTotalSteps,
  ]);

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleStart(e.clientX, e.clientY);
    },
    [handleStart]
  );

  const handleTouchStart = useCallback(
    (e) => {
      handleStart(e.touches[0].clientX, e.touches[0].clientY);
    },
    [handleStart]
  );

  const handleMouseMove = useCallback(
    (e) => {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    },
    [handleMove]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (
        Math.abs(e.touches[0].clientX - startXRef.current) >
        Math.abs(e.touches[0].clientY - startYRef.current)
      ) {
        e.preventDefault();
      }
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    },
    [handleMove]
  );

  useEffect(() => {
    const endHandler = () => handleEnd();
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("mouseup", endHandler);
    document.addEventListener("touchend", endHandler);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("mouseup", endHandler);
      document.removeEventListener("touchend", endHandler);
    };
  }, [handleMouseMove, handleTouchMove, handleEnd]);

  return (
    <div className="flex flex-col items-center gap-2">
      <span
        className={`text-sm font-medium ${
          isCurrentStepError ? "text-[#EF4056]" : "text-blue-600"
        }`}
      >
        {isDraggingRef.current ? dragStep : currentStep} of{" "}
        {effectiveTotalSteps}
      </span>
      <div
        ref={containerRef}
        className="flex items-center w-full relative h-8 overflow-hidden cursor-grab active:cursor-grabbing"
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          touchAction: "pan-y",
        }}
        draggable="false"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div
          className="absolute flex items-center"
          style={{
            transform: `translateX(${translateX}px)`,
            height: "100%",
          }}
          draggable="false"
        >
          {Array.from({ length: effectiveTotalSteps }, (_, index) => {
            const step = index + 1;
            const questionId = listQuestion[index]?.id;
            const isErrorStep = isReviewed && answerErrSteps.includes(step + 1);
            const hasAnswer =
              submittedSteps.includes(step) || !!questAnswers[questionId];

            return (
              <div
                key={`dot-${step}`}
                className="flex items-center justify-center px-2"
                style={{ width: "28px" }}
                draggable="false"
              >
                <ProgressDot
                  index={index}
                  currentStep={isDraggingRef.current ? dragStep : currentStep}
                  isReviewed={isReviewed}
                  isErrorStep={isErrorStep}
                  hasAnswer={hasAnswer}
                  onClick={() => onStepChange?.(step)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgressDotGroup;
